export enum UserConsentId {
  ToS = 'tos',
  Health = 'health',
  Privacy = 'privacy',
  Research = 'research',
  Marketing = 'marketing',
  // the same answer as the cookie dialog's Advertising choice (#5587). Decides
  // whether a web purchase is reported to Meta and Google Ads; see
  // `shared/src/ad-consent.ts`.
  Advertising = 'advertising',
}
export interface UserConsentDescription {
  id: UserConsentId;
  // English only. The app shows the translated `consent.user.<id>Label` instead
  // (see the consent dialog's consentLabel); this copy is the fallback for any
  // reader of the list that has no translations.
  label: string;
  url: string;
  optional: boolean;
}

export const userConsentTypes: UserConsentDescription[] = [
  {
    id: UserConsentId.ToS,
    label: 'Terms of Service',
    url: '/tos.html',
    optional: false,
  },
  {
    id: UserConsentId.Health,
    label: 'Health Disclaimer',
    url: '/health-disclaimer.html',
    optional: false,
  },
  {
    id: UserConsentId.Privacy,
    label: 'Privacy Policy',
    url: '/privacy.html',
    optional: false,
  },
  {
    id: UserConsentId.Research,
    label: 'Help improve the app by sharing anonymized fitness data for analysis.',
    url: '/privacy.html#research',
    optional: true,
  },
  {
    id: UserConsentId.Marketing,
    label: 'Stay updated with app tips, feature news, and occasional offers.',
    url: '/privacy.html#marketing',
    optional: true,
  },
  {
    id: UserConsentId.Advertising,
    label: 'Tell Meta and Google Ads when I start or pay for a subscription, so MH Physique can measure its ads.',
    url: '/privacy.html#advertising',
    optional: true,
  },
];

/**
 * What the Terms screen sends: every box the person ticked, plus an explicit
 * `false` for each optional box they left unticked (#5587).
 *
 * The explicit false matters. The server leaves an optional consent it is given
 * no true/false for exactly as the account already holds it, so an unticked
 * Advertising box that was simply left out would keep an older "yes" instead of
 * recording the "no" the person just gave.
 */
export function withUnansweredOptionalAsNo(accepts: { [key: string]: boolean }): { [key: string]: boolean } {
  const answered = { ...accepts };
  for (const ct of userConsentTypes) {
    if (ct.optional && answered[ct.id] !== true) answered[ct.id] = false;
  }
  return answered;
}

export interface UserConsentRecord {
  accepted: boolean;
  timestamp_utc: string;
  appVersion: string;
  /**
   * Which version of the Terms / Privacy text the app showed when this answer was
   * given — `CONSENT_TEXT_VERSION` in `consent-refresh.ts` (#850). Absent on every
   * record an app older than the version number wrote, which is what makes those
   * accounts be asked again.
   */
  textVersion?: number;
  /**
   * Only ever on the Terms record: when the app first asked this account to accept
   * a newer text, and which version that was. The month in which the question can
   * still be put off is counted from here, so it starts when the person is first
   * asked rather than on a date fixed in advance. Dropped the moment the Terms are
   * accepted again, because that save replaces the whole record.
   */
  refreshAsked?: { textVersion: number; at_utc: string };
}

export interface UserConsentRecords {
  [key: string]: UserConsentRecord; // keyed by ids from userConsentTypes
}

export interface SetUserConsentParams {
  consents: { [key: string]: boolean }; // keyed by ids from userConsentTypes
  appVersion: string;
  /**
   * Change ONLY the optional consents named in `consents`, and leave every other
   * record on the account as it is (#5587). Used by the cookie preferences dialog,
   * which knows the Advertising answer and nothing about the Terms. Without it the
   * call replaces the whole set, which is what the Terms screen wants.
   *
   * An older server ignores this field and refuses the call for the missing
   * mandatory consents, so it fails without saving anything.
   */
  onlyOptional?: boolean;
  /**
   * The version of the Terms / Privacy text the app showed with this answer
   * (#850). The server stores it on every record it writes. It comes from the app,
   * never from the server's own constant: an old app talking to a new server has
   * not shown the new text, and must not be recorded as if it had.
   */
  textVersion?: number;
  /**
   * Save nothing but "this account was asked to accept text version N, starting
   * now" on its Terms record (#850). `consents` is ignored and should be `{}`. The
   * server keeps an earlier mark for the same version, so calling it twice does
   * not restart the month.
   *
   * An older server ignores this field and refuses the call for the missing
   * mandatory consents, so it fails without saving anything.
   */
  markRefreshAsked?: number;
}

export interface SetUserConsentResults {
  error: string /** empty if all is ok */;
  // actual consents stored in the user's account are returned so that the caller can update the local copy
  consentRecords?: UserConsentRecords;
}
